import type { MerchantApiClient } from "../../types.js";
import type {
  MonitoringFilters,
  MonitoringObservationPage,
  MonitoringSettingsDraft,
  MonitoringSummary,
  MonitoringRunNowResult,
  NotificationPreferenceRecord,
} from "./models.js";

interface ListEnvelope<T> {
  items: T[];
  nextCursor: string | null;
  counts?: MonitoringSummary["counts"];
  planLimits?: MonitoringSummary["planLimits"];
  meta: { requestId: string };
}

interface DataEnvelope<T> {
  data: T;
  meta: { requestId: string };
}

export interface MonitoringApi {
  getNotificationPreferences(): Promise<NotificationPreferenceRecord>;
  getObservationHistory(
    mappingId: string,
    productId: string,
    month: string,
    limit: number,
    cursor?: string,
  ): Promise<MonitoringObservationPage>;
  getSummary(filters?: MonitoringFilters): Promise<MonitoringSummary>;
  runNow(
    mappingId: string,
    productId: string,
    idempotencyKey: string,
  ): Promise<MonitoringRunNowResult>;
  updateNotificationPreferences(
    preferences: NotificationPreferenceRecord,
    idempotencyKey: string,
  ): Promise<NotificationPreferenceRecord>;
  updateSettings(
    draft: MonitoringSettingsDraft,
    idempotencyKey: string,
  ): Promise<MonitoringSummary | MonitoringSummary["mappings"][number]>;
}

export function createMonitoringApi(client: MerchantApiClient): MonitoringApi {
  return {
    async getNotificationPreferences() {
      return unwrap(
        await client.get<DataEnvelope<NotificationPreferenceRecord>>(
          "/v1/api/notification-preferences",
        ),
      );
    },
    async getObservationHistory(mappingId, productId, month, limit, cursor) {
      const envelope = await client.get<
        ListEnvelope<MonitoringObservationPage["observations"][number]>
      >(
        `/v1/api/mappings/${encodeURIComponent(mappingId)}/monitoring/observations?${query({ productId, month, limit, cursor })}`,
      );
      return { observations: envelope.items, nextCursor: envelope.nextCursor };
    },
    async getSummary(filters) {
      const envelope = await client.get<ListEnvelope<MonitoringSummary["mappings"][number]>>(
        `/v1/api/monitoring?${query((filters ?? {}) as unknown as Record<string, unknown>)}`,
      );
      return {
        mappings: envelope.items,
        nextCursor: envelope.nextCursor,
        counts: envelope.counts ?? {
          totalVerified: envelope.items.length,
          enabled: 0,
          due: 0,
          healthy: 0,
          failing: 0,
          changed: 0,
          stale: 0,
        },
        planLimits: envelope.planLimits ?? {
          maxMonitoredMappings: envelope.items.length,
          allowedCadences: ["DAILY"],
        },
      };
    },
    async runNow(mappingId, productId, idempotencyKey) {
      return unwrap(
        await client.post<DataEnvelope<MonitoringRunNowResult>>(
          `/v1/api/mappings/${encodeURIComponent(mappingId)}/monitoring/check`,
          { productId },
          { idempotencyKey },
        ),
      );
    },
    async updateNotificationPreferences(preferences, idempotencyKey) {
      return unwrap(
        await client.put<DataEnvelope<NotificationPreferenceRecord>>(
          "/v1/api/notification-preferences",
          { rules: preferences.rules },
          { idempotencyKey },
        ),
      );
    },
    async updateSettings(draft, idempotencyKey) {
      const body = {
        mappingId: draft.mappingId,
        productId: draft.productId,
        enabled: draft.enabled,
        cadence: draft.cadence,
        timezone: draft.timezone,
      };
      return draft.mappingId === undefined
        ? toSummary(
            await client.put<ListEnvelope<MonitoringSummary["mappings"][number]>>(
              "/v1/api/monitoring/settings",
              body,
              { idempotencyKey },
            ),
          )
        : unwrap(
            await client.put<DataEnvelope<MonitoringSummary["mappings"][number]>>(
              "/v1/api/monitoring/settings",
              body,
              { idempotencyKey },
            ),
          );
    },
  };
}

function unwrap<T>(envelope: DataEnvelope<T>): T {
  return envelope.data;
}

function toSummary(
  envelope: ListEnvelope<MonitoringSummary["mappings"][number]>,
): MonitoringSummary {
  return {
    mappings: envelope.items,
    nextCursor: envelope.nextCursor,
    counts: envelope.counts ?? {
      totalVerified: envelope.items.length,
      enabled: envelope.items.filter((item) => item.monitoringEnabled).length,
      due: 0,
      healthy: 0,
      failing: 0,
      changed: 0,
      stale: 0,
    },
    planLimits: envelope.planLimits ?? {
      maxMonitoredMappings: envelope.items.length,
      allowedCadences: ["DAILY"],
    },
  };
}

function query(input: Record<string, unknown>): string {
  return new URLSearchParams(
    Object.entries(input)
      .filter(([, value]) => value !== undefined && value !== "")
      .map(([key, value]) => [key, String(value)]),
  ).toString();
}
